/**
 * Pure, framework-free helpers for reading role claims off a decoded JWT or
 * an MSAL `idTokenClaims` object.
 *
 * Split out of AuthContext.tsx so the cold-load fallback (see AuthContext's
 * `resolvedIsAdmin` effect) can decode a fresh `AuthenticationResult` the
 * exact same way the fast-path snapshot is read, with no MSAL/React
 * dependency of its own.
 */

/**
 * Base64URL-decodes segment 1 (the payload) of a JWT and JSON.parses it.
 * Never throws — any malformed input (wrong segment count, bad base64,
 * invalid JSON, absent `atob`) resolves to `null`.
 */
export function decodeJwtClaims(
  token: string | undefined | null
): Record<string, unknown> | null {
  if (!token) return null;
  if (typeof atob !== 'function') return null;

  const segments = token.split('.');
  if (segments.length !== 3) return null;

  const payload = segments[1];
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = atob(padded);
    const parsed: unknown = JSON.parse(decoded);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * True when `claims` carries the 'Admin' role via either the standard
 * `roles` array claim or the B2C custom `extension_Roles` claim
 * (comma-separated string or array form). Behaviour must stay identical to
 * the logic this was moved out of in AuthContext.tsx — do not add new claim
 * names here.
 */
export function hasAdminRole(claims: Record<string, unknown> | undefined | null): boolean {
  if (!claims) return false;

  const roles = claims['roles'];
  if (Array.isArray(roles) && roles.includes('Admin')) return true;

  const extRoles = claims['extension_Roles'];
  if (typeof extRoles === 'string') {
    return extRoles.split(',').map((r) => r.trim()).includes('Admin');
  }
  if (Array.isArray(extRoles) && extRoles.includes('Admin')) return true;

  return false;
}

/**
 * Discriminates "this token genuinely carries no role claim" from "this
 * token says you are not an admin". True when `claims` is a non-null object
 * that has the `roles` key OR the `extension_Roles` key present, regardless
 * of value — an empty array or empty string still counts as a source.
 */
export function hasRoleClaimSource(claims: Record<string, unknown> | undefined | null): boolean {
  if (!claims || typeof claims !== 'object') return false;
  return 'roles' in claims || 'extension_Roles' in claims;
}
